import { useEffect, useRef, useState } from "react";

import { searchLocalICD10, type ICD10Code } from "@/lib/icd10-common";
import { searchFullICD10 } from "@/lib/icd10-full";

const MIN_QUERY = 2;
const LOCAL_MAX = 8;
const TOTAL_MAX = 25;
const DEBOUNCE_MS = 300;

/**
 * Blended ICD-10 search shared by every manual diagnosis picker. Mirrors the
 * medication search (`medications/search.tsx`): the bundled common-codes list
 * (`searchLocalICD10`) shows instantly, then the full ICD-10-CM library
 * (`searchFullICD10`, NLM API) is fetched on a debounce, de-duped, and appended.
 *
 * `excludeCodes` are codes already shown/selected elsewhere (e.g. suggestion
 * chips) — filtered out of both tiers. On network failure the local results
 * remain. `loading` is true only while the remote fetch is in flight.
 */
export function useIcdSearch(
    query: string,
    excludeCodes: Set<string>
): { results: ICD10Code[]; loading: boolean } {
    const [results, setResults] = useState<ICD10Code[]>([]);
    const [loading, setLoading] = useState(false);

    // Stable key so the effect only re-runs when membership actually changes.
    const excludeKey = Array.from(excludeCodes).sort().join(",");
    // Latest-query guard so a slow response for an old query can't overwrite.
    const seqRef = useRef(0);

    useEffect(() => {
        const q = query.trim();
        if (q.length < MIN_QUERY) {
            setResults([]);
            setLoading(false);
            return;
        }

        const seq = ++seqRef.current;

        // Tier 1 — instant local common codes.
        const local = searchLocalICD10(q, LOCAL_MAX * 2)
            .filter((r) => !excludeCodes.has(r.code))
            .slice(0, LOCAL_MAX);
        setResults(local);
        setLoading(true);

        // Tier 2 — full NLM ICD-10-CM library, debounced, appended.
        const localCodes = new Set(local.map((r) => r.code));
        const timer = setTimeout(async () => {
            const full = await searchFullICD10(q, TOTAL_MAX);
            if (seq !== seqRef.current) {
                return; // superseded by a newer query
            }
            const remote = full.filter((r) => !excludeCodes.has(r.code) && !localCodes.has(r.code));
            setResults([...local, ...remote].slice(0, TOTAL_MAX));
            setLoading(false);
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
        // excludeKey is the stable stand-in for excludeCodes membership.
    }, [query, excludeKey]);

    return { results, loading };
}
